import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { CheckCircle, Loader2, CalendarDays, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const insuranceTypes = [
  "Home Insurance", "Auto Insurance", "Commercial Insurance", "Life Insurance",
  "Health Insurance", "Annuities", "LegalShield", "Long-term Care", "Travel Insurance", "General Consultation"
];

export default function AppointmentsPage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", insurance_type: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = selectedDate.toISOString().split("T")[0];
    setLoadingSlots(true);
    setSelectedSlot("");
    axios.get(`${API}/appointments/available-slots?date=${dateStr}`)
      .then((res) => setAvailableSlots(res.data.available_slots))
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) return;
    setSubmitting(true);
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      await axios.post(`${API}/appointments`, {
        ...formData,
        date: dateStr,
        time_slot: selectedSlot,
      });
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", insurance_type: "", notes: "" });
      setSelectedDate(null);
      setSelectedSlot("");
    } catch (e) {
      console.error("Booking failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const disabledDays = [
    { before: today },
    { dayOfWeek: [0, 6] },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4 animate-fade-up">Schedule an Appointment</h1>
          <p className="text-white/70 text-base lg:text-lg max-w-2xl mx-auto animate-fade-up delay-100">
            Book a time to speak with one of our insurance experts. We'll help you find the perfect coverage.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 sm:py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {success ? (
            <Card data-testid="appointment-success" className="bg-white rounded-xl max-w-lg mx-auto">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#002147] mb-3">Appointment Booked!</h3>
                <p className="text-slate-500 mb-6">
                  Your appointment has been scheduled. We'll send a confirmation to your email.
                </p>
                <Button
                  data-testid="book-another-btn"
                  onClick={() => setSuccess(false)}
                  className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold"
                >
                  Book Another Appointment
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Calendar */}
              <Card className="bg-white rounded-xl">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-[#002147] mb-1 flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-[#D4AF37]" />
                    Select a Date
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">Choose a weekday for your appointment</p>
                  <Calendar
                    data-testid="appointment-calendar"
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    className="rounded-md border border-slate-200"
                  />

                  {selectedDate && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-[#002147] text-sm mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#D4AF37]" />
                        Available Time Slots
                      </h4>
                      {loadingSlots ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin text-[#002147]" />
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <p className="text-slate-400 text-sm py-4">No slots available for this date.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              data-testid={`slot-${slot.replace(/\s/g, '-')}`}
                              onClick={() => setSelectedSlot(slot)}
                              className={`py-2 px-3 text-xs font-medium rounded-md border transition-all ${
                                selectedSlot === slot
                                  ? "bg-[#002147] text-white border-[#002147]"
                                  : "bg-white text-[#002147] border-slate-200 hover:border-[#D4AF37]"
                              }`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Form */}
              <Card className="bg-white rounded-xl">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-[#002147] mb-4">Your Information</h3>
                  <form onSubmit={handleSubmit} data-testid="appointment-form" className="space-y-5">
                    <div>
                      <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Full Name *</label>
                      <Input
                        data-testid="appt-input-name"
                        required
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Your full name"
                        className="h-12 border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Email *</label>
                      <Input
                        data-testid="appt-input-email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="john@example.com"
                        className="h-12 border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Phone *</label>
                      <Input
                        data-testid="appt-input-phone"
                        required
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="(219) 555-0123"
                        className="h-12 border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Insurance Type</label>
                      <Select value={formData.insurance_type} onValueChange={(v) => handleChange("insurance_type", v)}>
                        <SelectTrigger data-testid="appt-select-type" className="h-12 border-slate-300">
                          <SelectValue placeholder="What do you need help with?" />
                        </SelectTrigger>
                        <SelectContent>
                          {insuranceTypes.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Notes</label>
                      <Textarea
                        data-testid="appt-input-notes"
                        value={formData.notes}
                        onChange={(e) => handleChange("notes", e.target.value)}
                        placeholder="Any details you'd like us to know..."
                        className="min-h-[80px] border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                      />
                    </div>

                    {selectedDate && selectedSlot && (
                      <div data-testid="selected-datetime" className="bg-[#002147]/5 rounded-lg p-4">
                        <p className="text-sm font-medium text-[#002147]">
                          Selected: {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} at {selectedSlot}
                        </p>
                      </div>
                    )}

                    <Button
                      data-testid="submit-appointment-btn"
                      type="submit"
                      disabled={submitting || !selectedDate || !selectedSlot}
                      className="w-full bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold h-12"
                    >
                      {submitting ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Booking...</>
                      ) : (
                        "Book Appointment"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
